<template>
  <section>
    <div class="release-toolbar">
      <a-select
        v-model:value="store.target"
        aria-label="筛选平台"
        :options="[{ value: '', label: '全部平台' }, ...targets]"
      />
      <a-select
        v-model:value="store.channel"
        aria-label="筛选渠道"
        :options="[
          { value: '', label: '全部渠道' },
          { value: 'stable', label: '正式版' },
          { value: 'test', label: '测试版' },
        ]"
      />
      <a-select v-model:value="store.status" aria-label="筛选状态" :options="statusOptions" />
      <a-button :loading="store.loading" @click="store.load()"
        ><i class="ri-refresh-line" aria-hidden="true" />刷新</a-button
      >
      <a-button v-if="canManage" type="primary" @click="edit()"
        ><i class="ri-add-line" aria-hidden="true" />新增版本</a-button
      >
    </div>
    <p class="release-note">
      上传后保留为草稿；发布并指定官网版本后开放下载。下架当前版本不会自动回退。客户端自动更新另行发布。
    </p>
    <a-alert v-if="store.error" type="error" show-icon :message="store.error" />
    <a-table
      :data-source="store.visibleItems"
      :columns="columns"
      :loading="store.loading"
      row-key="id"
      :scroll="{ x: 1100 }"
      :pagination="{ pageSize: 20 }"
      :locale="{ emptyText: '暂无客户端版本' }"
    >
      <template #bodyCell="{ column, record }">
        <span v-if="column.key === 'target'">{{ targetLabel(record.target) }}</span>
        <span v-else-if="column.key === 'channel'">{{
          record.channel === 'test' ? '测试版' : '正式版'
        }}</span>
        <div v-else-if="column.key === 'status'">
          <a-tag :color="record.status === 'published' ? 'green' : 'default'">{{
            statusLabel(record.status)
          }}</a-tag
          ><a-tag v-if="record.isCurrent" color="blue">官网当前版本</a-tag>
        </div>
        <div v-else-if="column.key === 'file'" class="release-file">
          <template v-if="record.file"
            ><span>{{ record.file.name }}</span
            ><small
              >{{ (record.file.size / 1024 ** 2).toFixed(1) }} MiB · SHA-256 已校验</small
            ></template
          >
          <span v-else-if="record.delivery === 'external'">官方链接</span>
          <span v-else>尚未上传安装包</span>
          <template
            v-if="store.uploads[record.id] && store.uploads[record.id]!.phase !== 'cancelled'"
          >
            <a-progress
              :percent="progress(record.id)"
              :status="store.uploads[record.id]!.phase === 'failed' ? 'exception' : undefined"
              size="small"
            />
            <small
              v-if="store.uploads[record.id]!.phase === 'uploading' && progress(record.id) === 100"
              >正在校验并保存安装包…</small
            >
            <small v-if="store.uploads[record.id]!.error" role="alert">{{
              store.uploads[record.id]!.error
            }}</small>
          </template>
        </div>
        <span v-else-if="column.key === 'date'">{{
          record.publishedAt ? new Date(record.publishedAt).toLocaleString('zh-CN') : '—'
        }}</span>
        <a-space v-else-if="column.key === 'actions' && canManage" wrap>
          <template v-if="record.status === 'draft'">
            <a-button size="small" :disabled="uploading(record.id)" @click="edit(record)"
              >编辑</a-button
            >
            <label
              v-if="record.delivery === 'file' && !record.file && !openUpload(record.id)"
              class="release-upload"
            >
              上传安装包<input
                type="file"
                :accept="extension(record.target)"
                :aria-label="`为 ${targetLabel(record.target)} ${record.version} 上传安装包`"
                @change="selectFile(record, $event)"
              />
            </label>
            <a-button
              size="small"
              danger
              :disabled="uploading(record.id)"
              @click="confirm(record, 'delete')"
              >删除草稿</a-button
            >
          </template>
          <a-button
            v-if="
              store.uploads[record.id]?.phase === 'failed' &&
              store.uploads[record.id]?.id &&
              store.uploads[record.id]?.file
            "
            size="small"
            @click="store.retry(record.id)"
            >重试上传</a-button
          >
          <a-button
            v-if="
              ['uploading', 'failed'].includes(store.uploads[record.id]?.phase || '') &&
              store.uploads[record.id]?.id
            "
            size="small"
            @click="cancel(record.id)"
            >取消上传</a-button
          >
          <a-button
            v-if="record.status !== 'published'"
            size="small"
            type="primary"
            :disabled="uploading(record.id) || (record.delivery === 'file' && !record.file)"
            @click="confirm(record, 'publish')"
            >发布并设为官网版本</a-button
          >
          <a-button
            v-if="record.status === 'published' && !record.isCurrent"
            size="small"
            @click="confirm(record, 'set-current')"
            >设为官网版本</a-button
          >
          <a-button
            v-if="record.status === 'published'"
            size="small"
            danger
            @click="confirm(record, 'archive')"
            >下架</a-button
          >
        </a-space>
      </template>
      <template #expandedRowRender="{ record }">
        <a-descriptions title="版本详情" :column="1" size="small">
          <a-descriptions-item label="更新说明"
            ><span class="release-details">{{
              record.notes || '暂无更新说明'
            }}</span></a-descriptions-item
          >
          <a-descriptions-item v-if="record.delivery === 'external'" label="官方链接"
            ><span class="release-details">{{ record.externalUrl }}</span></a-descriptions-item
          >
          <a-descriptions-item v-if="record.file" label="SHA-256"
            ><span class="release-details">{{ record.file.sha256 }}</span></a-descriptions-item
          >
        </a-descriptions>
      </template>
    </a-table>
    <ClientReleaseForm :open="formOpen" :existing="editing" @close="formOpen = false" />
    <a-modal
      ok-text="确认"
      cancel-text="取消"
      :open="Boolean(action)"
      :title="actionTitle"
      :confirm-loading="acting"
      :ok-button-props="{ danger: action === 'delete' }"
      @ok="perform"
      @cancel="action = null"
    >
      <p>{{ selected ? `${targetLabel(selected.target)} · ${selected.version}` : '' }}</p>
      <p v-if="action === 'archive' && selected?.isCurrent">
        下架后官网显示“即将上线”，不会自动回退到旧版本。
      </p>
      <label for="release-action-reason">操作说明</label>
      <a-textarea id="release-action-reason" v-model:value="reason" :maxlength="500" :rows="3" />
      <a-alert v-if="actionError" type="error" :message="actionError" style="margin-top: 12px" />
    </a-modal>
  </section>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { message } from 'ant-design-vue';
import { CLIENT_RELEASE_TARGETS, type ClientReleaseView } from 'shared';
import ClientReleaseForm from '@/components/ClientReleaseForm.vue';
import { useClientReleasesStore } from '@/stores/client-releases.store';
import { useAdminSessionStore } from '@/stores/admin-session.store';
const store = useClientReleasesStore();
const session = useAdminSessionStore();
const canManage = computed(() => session.can('settings.manage'));
const targets = CLIENT_RELEASE_TARGETS.map((item) => ({ value: item.id, label: item.label }));
const statusOptions = [
  { value: '', label: '全部状态' },
  { value: 'draft', label: '草稿' },
  { value: 'published', label: '已发布' },
  { value: 'archived', label: '已下架' },
];
const columns = [
  { title: '平台', key: 'target' },
  { title: '版本号', dataIndex: 'version' },
  { title: '渠道', key: 'channel' },
  { title: '状态', key: 'status' },
  { title: '安装包／链接', key: 'file', width: 280 },
  { title: '发布时间', key: 'date' },
  { title: '操作', key: 'actions', width: 300 },
];
const formOpen = ref(false);
const editing = ref<ClientReleaseView>();
const selected = ref<ClientReleaseView>();
const action = ref<'publish' | 'set-current' | 'archive' | 'delete' | null>(null);
const reason = ref('');
const acting = ref(false);
const actionError = ref('');
const titles = {
  publish: '发布并设为官网版本',
  'set-current': '切换官网版本',
  archive: '下架版本',
  delete: '删除草稿及安装包',
};
const actionTitle = computed(() => (action.value ? titles[action.value] : '版本操作'));
function targetLabel(target: string) {
  return CLIENT_RELEASE_TARGETS.find((item) => item.id === target)?.label || target;
}
function extension(target: string) {
  return CLIENT_RELEASE_TARGETS.find((item) => item.id === target)?.extension;
}
function statusLabel(status: string) {
  return statusOptions.find((item) => item.value === status)?.label || status;
}
function uploading(id: string) {
  return store.uploads[id]?.phase === 'uploading';
}
function openUpload(id: string) {
  return (
    Boolean(store.uploads[id]?.id) &&
    ['uploading', 'failed'].includes(store.uploads[id]?.phase || '')
  );
}
function progress(id: string) {
  const item = store.uploads[id];
  return item ? Math.floor((item.offset / item.size) * 100) : 0;
}
function edit(item?: ClientReleaseView) {
  editing.value = item;
  formOpen.value = true;
}
function confirm(item: ClientReleaseView, next: NonNullable<typeof action.value>) {
  selected.value = item;
  action.value = next;
  reason.value = '';
  actionError.value = '';
}
async function perform() {
  if (!reason.value.trim()) {
    actionError.value = '请填写操作说明';
    return;
  }
  acting.value = true;
  try {
    await store.act(selected.value!, action.value!, reason.value);
    action.value = null;
  } catch (cause) {
    actionError.value = (cause as Error).message;
  } finally {
    acting.value = false;
  }
}
async function selectFile(item: ClientReleaseView, event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  try {
    await store.upload(item, file);
  } catch (cause) {
    message.error((cause as Error).message);
  }
}
async function cancel(id: string) {
  try {
    await store.cancel(id);
  } catch (cause) {
    message.error((cause as Error).message);
  }
}
onMounted(() => store.load());
</script>
<style scoped>
.release-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.release-toolbar :deep(.ant-select) {
  min-width: 150px;
}
.release-note,
.release-file small {
  color: var(--color-text-secondary, #667085);
}
.release-file {
  display: grid;
  gap: 4px;
  overflow-wrap: anywhere;
}
.release-details {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.release-upload {
  display: inline-flex;
  position: relative;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 8px;
  line-height: 22px;
  cursor: pointer;
}
.release-upload:focus-within {
  outline: 2px solid #1677ff;
  outline-offset: 2px;
}
.release-upload input {
  position: absolute;
  inset: 0;
  width: 100%;
  opacity: 0;
  cursor: pointer;
}
</style>
